import { useEffect, useMemo, useRef, useState } from 'react';
import { LuBuilding2, LuChevronDown, LuPlus, LuSearch, LuX } from 'react-icons/lu';
import aviso, { textoDoErro } from '../lib/aviso';
import { OrganizationService, type Organization } from '../services/OrganizationService';
import './OrganizationPicker.css';

interface Props {
    /** Organização vinculada hoje, ou null. */
    value: number | null;
    /** Nome da vinculada — evita uma consulta só para mostrar o rótulo. */
    valueLabel?: string | null;
    onChange: (id: number | null, nome: string | null) => void;
    disabled?: boolean;
    id?: string;
}

/**
 * Campo de organização do painel do contato.
 *
 * Por que não é o `Select` comum: aqui o valor pode **não existir ainda**. O
 * risco número um da fase 1 é o campo nascer vazio porque nenhum canal de
 * entrada traz o nome da empresa — então o campo cria a organização no
 * momento em que alguém tem a informação na mão, sem sair do painel.
 *
 * A lista mostra o parecido ANTES de oferecer criar: é o mesmo princípio da
 * regra de duplicata (avisar cedo, bloquear só no CNPJ).
 * Ver docs/propostas/organizacoes-fase-1.html §3.1.
 */
const OrganizationPicker = ({ value, valueLabel, onChange, disabled, id }: Props) => {
    const [aberto, setAberto] = useState(false);
    const [termo, setTermo] = useState('');
    const [achadas, setAchadas] = useState<Organization[]>([]);
    const [buscando, setBuscando] = useState(false);
    const [criando, setCriando] = useState(false);
    const ref = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        if (!aberto) return;
        const fora = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setAberto(false);
        };
        document.addEventListener('mousedown', fora);
        return () => document.removeEventListener('mousedown', fora);
    }, [aberto]);

    useEffect(() => {
        if (aberto) inputRef.current?.focus();
        else { setTermo(''); setAchadas([]); }
    }, [aberto]);

    useEffect(() => {
        if (!aberto) return;

        const alvo = termo.trim();
        if (alvo.length < 2) { setAchadas([]); return; }

        setBuscando(true);
        const t = window.setTimeout(() => {
            OrganizationService.search(alvo)
                .then(setAchadas)
                .catch(() => setAchadas([]))
                .finally(() => setBuscando(false));
        }, 300);

        return () => { window.clearTimeout(t); setBuscando(false); };
    }, [termo, aberto]);

    /* Só oferece criar quando o que foi digitado não é exatamente uma das
       achadas — senão a porta de criar duplicata fica ao lado da original. */
    const podeCriar = useMemo(() => {
        const alvo = termo.trim();
        if (alvo.length < 2) return false;
        return !achadas.some(o => o.nome.trim().toLowerCase() === alvo.toLowerCase());
    }, [termo, achadas]);

    const escolher = (o: Organization) => {
        onChange(o.organizacaoId, o.nome);
        setAberto(false);
    };

    const criar = async () => {
        const nome = termo.trim();
        if (!nome) return;
        setCriando(true);
        try {
            const resposta = await OrganizationService.create({ nome });
            const nova = resposta.dados;
            aviso.ok(`Organização "${nome}" criada.`);
            onChange(nova?.organizacaoId ?? null, nova?.nome ?? nome);
            setAberto(false);
        } catch (e) {
            aviso.erro(textoDoErro(e, 'Não foi possível criar a organização.'));
        } finally {
            setCriando(false);
        }
    };

    return (
        <div className="og-pick" ref={ref}>
            <button
                type="button"
                id={id}
                className={`og-pick-trigger ${aberto ? 'is-open' : ''}`}
                onClick={() => setAberto(a => !a)}
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={aberto}
            >
                <LuBuilding2 size={13} className="og-pick-ico" />
                <span className={`og-pick-label ${value ? '' : 'is-empty'}`}>
                    {value ? (valueLabel || 'Organização') : 'Sem organização'}
                </span>
                {value && !disabled && (
                    <span
                        role="button"
                        tabIndex={0}
                        className="og-pick-clear"
                        aria-label="Remover vínculo com a organização"
                        title="Remover vínculo"
                        onClick={e => { e.stopPropagation(); onChange(null, null); }}
                        onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault(); e.stopPropagation(); onChange(null, null);
                            }
                        }}
                    >
                        <LuX size={11} />
                    </span>
                )}
                <LuChevronDown size={13} className="og-pick-chev" />
            </button>

            {aberto && (
                <div className="og-pick-pop" role="listbox">
                    <label className="og-pick-search">
                        <LuSearch size={13} />
                        <input
                            ref={inputRef}
                            type="search"
                            value={termo}
                            placeholder="Buscar ou criar organização…"
                            onChange={e => setTermo(e.target.value)}
                        />
                    </label>

                    <div className="og-pick-list">
                        {termo.trim().length < 2 && (
                            <p className="og-pick-dica">Digite ao menos duas letras.</p>
                        )}

                        {termo.trim().length >= 2 && buscando && (
                            <p className="og-pick-dica">Buscando…</p>
                        )}

                        {termo.trim().length >= 2 && !buscando && achadas.length === 0 && (
                            <p className="og-pick-dica">Nenhuma organização com esse nome.</p>
                        )}

                        {achadas.map(o => (
                            <button key={o.organizacaoId} type="button" className="og-pick-item"
                                onClick={() => escolher(o)}>
                                <span className="og-pick-item-nome">{o.nome}</span>
                                <span className="og-pick-item-meta">
                                    {o.quantidadePessoas > 0
                                        ? `${o.quantidadePessoas} ${o.quantidadePessoas === 1 ? 'pessoa' : 'pessoas'}`
                                        : 'sem pessoas'}
                                </span>
                            </button>
                        ))}
                    </div>

                    {podeCriar && (
                        <button type="button" className="og-pick-criar" onClick={criar} disabled={criando}>
                            <LuPlus size={12} />
                            {criando ? 'Criando…' : `Criar "${termo.trim()}"`}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default OrganizationPicker;
